import { DateTime } from "luxon";

/**
 * Locale-aware dates/times for the notifications queue (Luxon).
 *
 * Shared by list cells, details summary, error timelines, member DOB, and CSV export. Payload
 * fields are expected to be ISO-8601 strings consumable by `DateTime.fromISO`.
 */

/**
 * Formats a timestamp or calendar date for display using the browser’s locale (`Intl` via Luxon).
 *
 * Typical inputs: `sentTimestamp`, `scheduledTimestamp`, error `timestamp`, `memberDob`. Parses with `DateTime.fromISO`; an
 * invalid parse yields an em dash (`—`). For `includeTime: false`, shows only the calendar date
 * (no clock time).
 *
 * @param {unknown} value ISO string from the API (date-only or date-time).
 * @param {{ includeTime?: boolean }} [options] Pass `includeTime: false` for date-only display. Defaults to `true` (medium date + short time).
 * @returns {string} Localized string, or `—` when the value is empty or not a valid ISO date/time.
 */
export function formatNotificationLocaleDateTime(value, options = {}) {
    const includeTime = options.includeTime !== false;

    if (!value) {
        return "—";
    }

    const dt = DateTime.fromISO(value);
    if (!dt.isValid) {
        return "—";
    }

    return dt.toLocaleString(
        includeTime
            ? {
                  dateStyle: "medium",
                  timeStyle: "short",
              }
            : { dateStyle: "medium" },
    );
}

/**
 * Whole calendar years from a birth date string to today (local start-of-day for both ends).
 *
 * Uses the same `DateTime.fromISO` parsing as `formatNotificationLocaleDateTime` for DOB-shaped
 * values. Returns `null` when the input is missing, fails Luxon validation, or implies a future
 * birthday (negative age).
 *
 * @param {unknown} isoDateString Birth date (typically `YYYY-MM-DD` from the API).
 * @returns {number | null} Completed years of age, or `null` if not computable.
 */
export function getAgeInYearsFromYyyyMmDd(isoDateString) {
    if (!isoDateString) {
        return null;
    }

    const birth = DateTime.fromISO(isoDateString);
    if (!birth.isValid) {
        return null;
    }

    const today = DateTime.now().startOf("day");
    const birthDay = birth.startOf("day");
    const age = Math.floor(today.diff(birthDay, "years").years);

    if (age < 0) {
        return null;
    }

    return age;
}
